import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../api/store";
import { optionsUsers } from "../../data/group-insurance";
import { CategoryManageModal, CategoryPayload, CategoryTab, Mode, SearchControlPayload, SearchControlType, UpdateAccount } from "../../types/type";
import { CreateAccountFormState, Option, PasswordFormState } from "./user-form-state";
import { SystemDefault } from "../gms-proposal-forms/basic-information/basic-information.state";

const toSystemDefault = (option: Option): SystemDefault => ({
    id: option.id,
    name: option.name,
});

const initialState: CreateAccountFormState = {
    firstname: '',
    middlename: '',
    lastname: '',
    suffix: { id: 0, name: "None" },
    email: '',
    agent_code: '',
    role: { id: 0, name: "Select" },
    location: { id: 0, name: "Select" },
    department: { id: 0, name: "Select" },
    phoneNumber: '',
    reporting_to_id: { id: 0, name: "Select" },
    position: null,

    updateType: 'view',
    updateAccountId: null,
    accountSettingMode: 'view',

    updatePassword: {
        values: {
            password: "",
            newPassword: "",
            confirmPassword: "",
        },
        visible: {
            password: false,
            newPassword: false,
            confirmPassword: false,
        },
    },
    
    searchType: toSystemDefault(optionsUsers[0]),
    searchValue: '',
    isTyping: false,

    selectedCategoryTabs: 'roles',
    selectedCategoryId: null,
    categoryModalType: null,
    isCategoryModalShow: false,
    category: {
        name: '',
        code: '',
        is_active: false,
    }
};

export const createAccountSlice = createSlice({
    name: 'createAccount',
    initialState,
    reducers: { 
        setAccountSettingMode: ( state, action: PayloadAction<Mode>) => {
            state.accountSettingMode = action.payload;
        },
        setUpdatePasswordValue: ( state, action: PayloadAction<{ field: keyof PasswordFormState["values"]; value: string; }>) => {
            const { field, value } = action.payload;
            state.updatePassword.values[field] = value;
        },

        toggleUpdatePasswordVisibility: ( state, action: PayloadAction<keyof PasswordFormState["visible"]>) => {
            const field = action.payload;
            state.updatePassword.visible[field] =
                !state.updatePassword.visible[field];
        },
        resetUpdatePassword: (state) => {
            state.updatePassword = {
                values: {
                    password: "",
                    newPassword: "",
                    confirmPassword: "",
                },
                visible: {
                    password: false,
                    newPassword: false,
                    confirmPassword: false,
                },
            };
        },
        setUpdateMode: (state, action: PayloadAction<UpdateAccount>) => {
            switch (action.payload.type) {
                case 'actionType':
                    state.updateType = action.payload.value
                    break
                case 'acctId':
                    state.updateAccountId = action.payload.value
                    break
            }
        },
        setManageModal: (state, action: PayloadAction<CategoryManageModal>) => {
            switch (action.payload.type) {
                case 'id':
                    state.selectedCategoryId = action.payload.value
                    break
                case 'modalType':
                    state.categoryModalType = action.payload.value
                    break
                case 'isShow':
                    state.isCategoryModalShow = action.payload.value
                    break
            }
        },
        setCategory: (state, action: PayloadAction<CategoryPayload>) => {
            switch (action.payload.type) {
                case "name":
                    state.category.name = action.payload.value;
                    break;
                case "code":
                    state.category.code = action.payload.value;
                    break;
                case "is_active":
                    state.category.is_active = action.payload.value;
                    break;
            }
        },
        resetCategory: (state) => {
            state.category = { name: "", code: "", is_active: false };
        },
        setSelectedCategory: (state, action: PayloadAction<CategoryTab>) => {
            state.selectedCategoryTabs = action.payload;
        },
        setSearchControl: (state, action: PayloadAction<SearchControlPayload>) => {
            switch (action.payload.type) {
                case "sType":
                    state.searchType = action.payload.value;
                    break;
                case "sValue":
                    state.searchValue = action.payload.value;
                    break;
                case "sTyping":
                    state.isTyping = action.payload.value;
                    break;
            }
        },
        resetSearchControl: (state, action: PayloadAction<SearchControlType>) => {
            switch (action.payload) {
                case "sType":
                    state.searchType = optionsUsers[0];
                    break;
                case "sValue":
                    state.searchValue = "";
                    break;
                case "sTyping":
                    state.isTyping = false;
                    break;
            }
        },
        updateField: <T extends keyof CreateAccountFormState>(
            state: CreateAccountFormState,
            action: PayloadAction<{ field: T; value: CreateAccountFormState[T] }>
        ) => {
            const { field, value } = action.payload;
            if (field === 'phoneNumber' && typeof value === 'string') {
                // keep only digits
                let digitsOnly = value.replace(/\D/g, "");

                // if first digit exists, it must be 9
                if (digitsOnly.length > 0 && digitsOnly[0] !== "9") {
                    return;
                }

                // max 10 digits only
                digitsOnly = digitsOnly.slice(0, 10);

                state.phoneNumber = digitsOnly;
                return;
            }

            (state[field] as CreateAccountFormState[T]) = value;
        },

        resetForm: () => initialState,

        setForm: (state, action: PayloadAction<CreateAccountFormState>) => {
            return action.payload;
        },
    },
})

export const { 
    setAccountSettingMode,

    setUpdatePasswordValue,
    toggleUpdatePasswordVisibility,
    resetUpdatePassword,

    setUpdateMode,

    setCategory,
    resetCategory,

    setManageModal,
    
    setSelectedCategory,

    setSearchControl,
    resetSearchControl,

    updateField, 
    resetForm, 
    setForm 
} = createAccountSlice.actions;

export const selectCreateAccountState = (state: RootState) => state.createAccount;

export const selectSearchTypeState = (state: RootState) => state.createAccount.searchType;
export const selectSearchValueState = (state: RootState) => state.createAccount.searchValue;
export const selectIsTypingState = (state: RootState) => state.createAccount.isTyping;

export const selectSelectedTabs = (state: RootState) => state.createAccount.selectedCategoryTabs;

export const selectSelectedCategoryUpdateId = (state: RootState) => state.createAccount.selectedCategoryId;

export const selectSelectedCategory = (state: RootState) => state.createAccount.category;

export const selectIsCategoryModalShow = (state: RootState) => state.createAccount.isCategoryModalShow;

export const selectTypeOfModal = (state: RootState) => state.createAccount.categoryModalType;

export const selectUpdateActionType = (state: RootState) => state.createAccount.updateType;
export const selectUpdateAccountId = (state: RootState) => state.createAccount.updateAccountId;

export const selectUpdatePassword = (state: RootState) => state.createAccount.updatePassword;

export const selectAccountSettingMode = (state: RootState) => state.createAccount.accountSettingMode;

export default createAccountSlice.reducer;